import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static values = {
    labels: Array,
    series: Array,
    colors: { type: Array, default: [] },
    formatAsCurrency: { type: Boolean, default: false }
  }

  connect() {
    this.renderChart()
    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.mediaQuery.addListener(() => this.renderChart())
    this.observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === "class" && m.target === document.documentElement) this.renderChart()
      }
    })
    this.observer.observe(document.documentElement, { attributes: true, attributeFilter: [ "class" ] })
  }

  disconnect() {
    this.mediaQuery?.removeListener?.()
    this.observer?.disconnect()
    this.chart?.destroy()
  }

  renderChart() {
    this.chart?.destroy()
    const labels = this.labelsValue || []
    const series = this.seriesValue || []
    const defaultColors = [
      "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
      "#ec4899", "#06b6d4", "#84cc16"
    ]
    const colors = this.colorsValue?.length ? this.colorsValue : defaultColors.slice(0, labels.length)

    if (labels.length === 0 || series.length === 0) return

    const formatAsCurrency = this.formatAsCurrencyValue
    const isDark = document.documentElement.classList.contains("dark") ||
      window.matchMedia("(prefers-color-scheme: dark)").matches

    const labelColor = isDark ? "#e5e7eb" : "#374151"
    const totalLabelColor = isDark ? "#f3f4f6" : "#111827"

    const options = {
      chart: {
        type: "donut",
        height: 320,
        fontFamily: "inherit",
        toolbar: { show: false }
      },
      theme: { mode: isDark ? "dark" : "light" },
      stroke: { width: 0 },
      labels,
      series,
      colors,
      legend: {
        position: "bottom",
        fontSize: "13px",
        labels: { colors: labelColor }
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val?.toFixed(1) ?? 0}%`
      },
      tooltip: {
        y: {
          formatter: (val) => {
            const num = Number(val)
            if (formatAsCurrency && !Number.isNaN(num)) {
              return "$" + (num / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            }
            return !Number.isNaN(num) ? num.toLocaleString() : ""
          }
        }
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              name: { color: labelColor },
              value: {
                color: totalLabelColor,
                formatter: (val) => {
                  const num = Number(val)
                  if (formatAsCurrency && !Number.isNaN(num)) {
                    return "$" + (num / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  }
                  return !Number.isNaN(num) ? num.toLocaleString() : ""
                }
              },
              total: {
                show: true,
                label: "Total",
                color: totalLabelColor,
                formatter: () => {
                  const sum = series.reduce((a, b) => a + b, 0)
                  if (formatAsCurrency) {
                    return "$" + (sum / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                  }
                  return sum.toLocaleString()
                }
              }
            }
          }
        }
      }
    }

    this.chart = new ApexCharts(this.element, options)
    this.chart.render()
  }
}
